// A guided setup of an authenticator app (ADR 0039): chosen at registration,
// and required once an account has used its sign-ins with an SMS code (the
// administrator sets how many). A session in that state may do nothing else,
// so the shell sends it here; confirming the app refreshes the session into
// one that may.
import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Code,
  CopyButton,
  Group,
  List,
  Stack,
  Stepper,
  Text,
  TextInput,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import qrcode from "qrcode-generator";
import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { SignedIn } from "../../shared/session/guards";
import { safeNext } from "../../shared/session/invitation";
import { ME_KEY, useMe } from "../../shared/session/me";
import { ProofForm, useEnrolment, type Enrolment } from "../../shared/session/enrol";
import { refresh, useSession } from "../../shared/session/session";
import { FormError, Page } from "../../shared/ui/Page";

export function SetupAuthenticatorPage() {
  return (
    <SignedIn>
      <Setup />
    </SignedIn>
  );
}

function Setup() {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next")) ?? "/account";
  const [step, setStep] = useState(0);
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { needsProof, begin } = useEnrolment();
  const m = me.data;

  const start = async (password?: string) => {
    setBusy(true);
    setError(null);
    const r = await begin(password);
    setBusy(false);
    if (r.error) return setError(r.error);
    if (!r.enrolment) return;
    setEnrolment(r.enrolment);
    setStep(1);
  };
  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error } = await api.POST("/mfa/totp/confirm", { body: { code } });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setCodes(data.recovery_codes);
    setStep(3);
  };
  const finish = async () => {
    setBusy(true);
    // The session now carries "totp"; a fresh access token lifts the restriction.
    await refresh();
    await queryClient.invalidateQueries({ queryKey: ME_KEY });
    setBusy(false);
    navigate(next, { replace: true });
  };
  const signOut = async () => {
    await session.signOut();
    navigate("/signin", { replace: true });
  };

  // Already set up in another session: this one must sign in again with it.
  if (m?.totp_enabled && !codes) {
    return (
      <Page title={t("setup_totp.title")}>
        <Stack gap="md">
          <Alert color="green" data-testid="totp-already-on">
            {m.totp_setup_required ? t("setup_totp.already_sign_in_again") : t("setup_totp.already_on")}
          </Alert>
          {m.totp_setup_required ? (
            <Button onClick={() => void signOut()}>{t("nav.sign_out")}</Button>
          ) : (
            <Button onClick={() => navigate(next, { replace: true })}>{t("setup_totp.continue")}</Button>
          )}
        </Stack>
      </Page>
    );
  }

  return (
    <Page title={t("setup_totp.title")}>
      <Stack gap="md">
        {m?.totp_setup_required ? (
          <Alert color="orange" title={t("setup_totp.required_title")} data-testid="totp-setup-required">
            {t("setup_totp.required_body")}
          </Alert>
        ) : (
          <Text size="sm">{t("setup_totp.intro")}</Text>
        )}
        <Stepper active={step} orientation="vertical" size="sm">
          <Stepper.Step label={t("setup_totp.step_app")}>
            <Stack gap="sm">
              <Text size="sm">{t("setup_totp.app_body")}</Text>
              <List size="sm">
                <List.Item>Google Authenticator</List.Item>
                <List.Item>Aegis (Android)</List.Item>
                <List.Item>2FAS</List.Item>
                <List.Item>FreeOTP</List.Item>
              </List>
              <Text size="sm" c="dimmed">
                {t("setup_totp.app_any")}
              </Text>
              <FormError message={error} />
              {needsProof ? (
                <ProofForm busy={busy} onSubmit={(pw) => void start(pw)} />
              ) : (
                <Button w="fit-content" loading={busy} onClick={() => void start()}>
                  {t("setup_totp.have_app")}
                </Button>
              )}
            </Stack>
          </Stepper.Step>
          <Stepper.Step label={t("setup_totp.step_add")}>
            {enrolment && (
              <Stack gap="sm">
                <Text size="sm">{t("setup_totp.add_body")}</Text>
                <QRCode value={enrolment.otpauth_url} label={t("setup_totp.qr")} />
                <Anchor href={enrolment.otpauth_url} size="sm">
                  {t("setup_totp.on_this_phone")}
                </Anchor>
                <Text size="sm" fw={600}>
                  {t("setup_totp.key")}
                </Text>
                <Group gap="xs">
                  <Code fz="md" data-testid="totp-secret" style={{ wordBreak: "break-all" }}>
                    {enrolment.secret}
                  </Code>
                  <CopyButton value={enrolment.secret}>
                    {({ copied, copy }) => (
                      <Button size="xs" variant="default" onClick={copy}>
                        {copied ? t("common.copied") : t("common.copy")}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
                <Button w="fit-content" onClick={() => setStep(2)}>
                  {t("setup_totp.added")}
                </Button>
              </Stack>
            )}
          </Stepper.Step>
          <Stepper.Step label={t("setup_totp.step_code")}>
            <form onSubmit={confirm}>
              <Stack gap="sm">
                <TextInput
                  label={t("security.code")}
                  description={t("setup_totp.code_hint")}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.currentTarget.value.replace(/\s/g, ""))}
                  required
                />
                <FormError message={error} />
                <Group gap="xs">
                  <Button type="submit" loading={busy}>
                    {t("security.turn_on")}
                  </Button>
                  <Button variant="default" onClick={() => setStep(1)}>
                    {t("setup_totp.back")}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Stepper.Step>
          <Stepper.Step label={t("setup_totp.step_recovery")}>
            {codes && (
              <Stack gap="sm" data-testid="recovery-codes">
                <Text size="sm">{t("security.recovery_intro")}</Text>
                <Code block>{codes.join("\n")}</Code>
                <CopyButton value={codes.join("\n")}>
                  {({ copied, copy }) => (
                    <Button size="xs" variant="default" w="fit-content" onClick={copy}>
                      {copied ? t("common.copied") : t("security.copy_codes")}
                    </Button>
                  )}
                </CopyButton>
                <Checkbox
                  label={t("security.saved")}
                  checked={saved}
                  onChange={(e) => setSaved(e.currentTarget.checked)}
                />
                <Button w="fit-content" disabled={!saved} loading={busy} onClick={() => void finish()}>
                  {t("setup_totp.continue")}
                </Button>
              </Stack>
            )}
          </Stepper.Step>
        </Stepper>
      </Stack>
    </Page>
  );
}

/** QRCode draws the setup link for the authenticator app to scan. */
function QRCode({ value, label }: { value: string; label: string }) {
  const path = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(value);
    qr.make();
    const n = qr.getModuleCount();
    let d = "";
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        if (qr.isDark(r, c)) d += `M${c + 4} ${r + 4}h1v1h-1z`;
      }
    }
    return { d, size: n + 8 };
  }, [value]);
  return (
    <svg
      viewBox={`0 0 ${path.size} ${path.size}`}
      width={220}
      height={220}
      role="img"
      aria-label={label}
      data-testid="totp-qr"
      shapeRendering="crispEdges"
    >
      <rect width={path.size} height={path.size} fill="#fff" />
      <path d={path.d} fill="#000" />
    </svg>
  );
}
